// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useEffect, useRef } from 'react';
import InternalToken from '../../../token/internal';
import styles from './styles.css.js';
export function OverflowDropdown({ tokens, triggerRef, open, onDismiss, onClose, readOnly }) {
    const pendingFocusAfterDismissRef = useRef(null);
    const listRef = useRef(null);
    const panelRef = useRef(null);
    useEffect(() => {
        var _a;
        if (!open) {
            return;
        }
        const first = (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('button');
        first === null || first === void 0 ? void 0 : first.focus();
    }, [open]);
    // No deps: runs after every render to handle focus restoration after a token dismiss.
    useEffect(() => {
        var _a;
        if (pendingFocusAfterDismissRef.current === null) {
            return;
        }
        const dismissedIndex = pendingFocusAfterDismissRef.current;
        pendingFocusAfterDismissRef.current = null;
        if (tokens.length === 0) {
            return;
        }
        const nextPosition = Math.min(dismissedIndex, tokens.length - 1);
        const buttons = (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('button');
        if (buttons && buttons[nextPosition]) {
            buttons[nextPosition].focus();
        }
    });
    const handlePanelKeyDown = (e) => {
        var _a, _b, _c;
        if (e.key === 'Escape') {
            e.stopPropagation();
            onClose();
            (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        else if (e.key === 'Tab') {
            onClose();
        }
        else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            e.stopPropagation();
            const buttons = Array.from((_c = (_b = listRef.current) === null || _b === void 0 ? void 0 : _b.querySelectorAll('button')) !== null && _c !== void 0 ? _c : []);
            if (buttons.length === 0) {
                return;
            }
            const focused = buttons.indexOf(document.activeElement);
            if (e.key === 'ArrowUp') {
                const prev = focused > 0 ? focused - 1 : buttons.length - 1;
                buttons[prev].focus();
            }
            else {
                const next = focused < buttons.length - 1 ? focused + 1 : 0;
                buttons[next].focus();
            }
        }
    };
    useEffect(() => {
        if (!open) {
            return;
        }
        const handleClick = (e) => {
            if (panelRef.current &&
                !panelRef.current.contains(e.target) &&
                triggerRef.current &&
                !triggerRef.current.contains(e.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open, onClose, triggerRef]);
    if (!open) {
        return null;
    }
    return (React.createElement("div", { ref: panelRef, onMouseDown: e => e.preventDefault(), onKeyDown: handlePanelKeyDown, className: styles['overflow-panel'] },
        React.createElement("ul", { ref: listRef, role: "list", className: styles['overflow-panel-list'] }, tokens.map((token, i) => {
            var _a;
            return (React.createElement("li", { key: i, className: styles['overflow-panel-item'] },
                React.createElement(InternalToken, { label: token.value, dismissLabel: (_a = token.dismissLabel) !== null && _a !== void 0 ? _a : token.value, icon: token.icon, variant: "inline", readOnly: readOnly, onDismiss: () => {
                        pendingFocusAfterDismissRef.current = i;
                        onDismiss(i);
                    } })));
        }))));
}
//# sourceMappingURL=overflow-dropdown.js.map